import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {
  BoxCreationStructuredTable,
  BoxCreationTableMetadata,
  BoxCreationTableRowMetadata,
} from 'notebook/integrations/samples/BoxCreationStructuredTableAdapter';
import {ACTION_CREATE_BOXES} from 'notebook/store/VersioningConstants';
import {ActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import makeSimpleActionFunction, {
  getSubmitActionFunctionIcon,
  getSubmitButtonHidden,
  getSubmitButtonLabel,
  getSubmitButtonStyle,
} from 'notebook/structured-tables/action-functions/makeSimpleActionFunction';
import {
  ActionResultType,
  performServerSideTableWriteActionWithAudits,
  ServerSideWriteActionResult,
  batchedSubmissionGenerator,
  getSubmissionBatchSize,
  isBatchingRequired,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import isTableActionButtonDisabled from 'notebook/structured-tables/util/isTableActionButtonDisabled';
import tableActionButtonDisabledTooltip from 'notebook/structured-tables/util/tableActionButtonDisabledTooltip';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import notifications from 'notifications';
import {ButtonStyle} from 'ui-library/buttons/Button';
import APIUtil from 'util/APIUtil';

import {StructuredTableKind, TableActionFunctionParameters} from '../StructuredTableConstants';
import {getRowAwareLoadingMessage, RowSubmissionRange} from './makeSubmitRowActionFunction';

export const doPerformCreateBoxes = async (
  entryOrTemplateId: string,
  structuredTable: BoxCreationStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.CreateBoxesResponse>> => {
  const batchSize = getSubmissionBatchSize(StructuredTableKind.BOX_CREATION);
  if (isBatchingRequired(computedTable, batchSize, selection)) {
    return await attemptBatchedCreateBoxes(
      entryOrTemplateId,
      batchSize!,
      structuredTable,
      computedTable,
      actionFunctionContext,
      selection,
      updateStatus,
      dataModsHeader
    );
  } else {
    const pertinentComputedTable = selection
      ? computedTable.slice(selection.start, selection.end)
      : computedTable;

    const responseData = await attemptCreateBoxes(
      entryOrTemplateId,
      structuredTable,
      pertinentComputedTable,
      actionFunctionContext,
      selection,
      undefined,
      dataModsHeader
    );
    return {resultType: ActionResultType.SUCCESS, responseData};
  }
};

async function attemptCreateBoxes(
  entryOrTemplateId: string,
  structuredTable: BoxCreationStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  latestVersionId?: number,
  dataModsHeader?: DataModificationsHeader
): Promise<ActionFunctionDAO.CreateBoxesResponse> {
  return await ActionFunctionDAO.createBoxes(
    entryOrTemplateId,
    structuredTable,
    computedTable,
    actionFunctionContext,
    selection,
    latestVersionId,
    dataModsHeader
  );
}

async function attemptBatchedCreateBoxes(
  entryOrTemplateId: string,
  batchSize: number,
  structuredTable: BoxCreationStructuredTable,
  computedTable: Array<Array<EvaluatedCell>>,
  actionFunctionContext: ActionFunctionContext,
  selection?: RowSubmissionRange,
  updateStatus?: (msg: string) => void,
  dataModsHeader?: DataModificationsHeader
): Promise<ServerSideWriteActionResult<ActionFunctionDAO.CreateBoxesResponse>> {
  let responseData: ActionFunctionDAO.CreateBoxesResponse = {entryVersionId: -1};
  let latestVersionId: number | undefined;
  let partialSuccess = false;
  let submittedRange: RowSubmissionRange = {
    start: selection ? selection.start : 0,
    end: selection ? selection.start : 0,
  };
  const batches = batchedSubmissionGenerator(computedTable, batchSize, structuredTable, selection);

  try {
    for (const {range, evaluatedRowBatch} of batches) {
      if (updateStatus) {
        updateStatus(getRowAwareLoadingMessage(range, 'Updating boxes for rows', 'Updating box for row'));
      }
      const batchResult = await attemptCreateBoxes(
        entryOrTemplateId,
        structuredTable,
        evaluatedRowBatch,
        actionFunctionContext,
        range,
        latestVersionId,
        dataModsHeader
      );
      latestVersionId = batchResult.entryVersionId;
      partialSuccess = true;
      responseData = {entryVersionId: latestVersionId};
      submittedRange = {...submittedRange, end: range.end};
    }
  } catch (e) {
    if (partialSuccess) {
      notifications.error(notifications.getFriendlyError(e) ?? 'An unexpected error occured.');
      return {resultType: ActionResultType.PARTIAL_SUCCESS, responseData, submittedRange};
    }

    throw e;
  }

  return {resultType: ActionResultType.SUCCESS, responseData};
}

export async function onInvokeCreateBoxesActionFunction({
  entryOrTemplateId,
  structuredTable,
  structuredTableAdapter,
  computedTable,
  actionFunctionContext,
  table,
}: TableActionFunctionParameters<BoxCreationTableMetadata, BoxCreationTableRowMetadata>): Promise<void> {
  assert(
    entryOrTemplateId != null && APIUtil.isBaseEntry(entryOrTemplateId),
    'Create boxes action is only supported in entries.',
    {apiId: entryOrTemplateId}
  );

  await performServerSideTableWriteActionWithAudits({
    entryId: entryOrTemplateId,
    loadingScreenMessage: 'Updating boxes...',
    autoSaverAction: ACTION_CREATE_BOXES,
    doPerformAction: (updateStatus?: (msg: string) => void, dataModsHeader?: DataModificationsHeader) =>
      doPerformCreateBoxes(
        entryOrTemplateId,
        structuredTable as BoxCreationStructuredTable,
        computedTable,
        actionFunctionContext,
        undefined, // selection
        updateStatus,
        dataModsHeader
      ),
    analyticsActionForPerfTracking: 'box_creation_table_create',
    structuredTables: [structuredTable],
    structuredTableAdapters: [structuredTableAdapter],
    computedTables: [computedTable],
    doPostRefresh: (actionResult) => {
      invokeStructuredTableMappings(
        entryOrTemplateId,
        structuredTable,
        // on a partial success we want to map only the rows that were successfully submitted
        actionResult.resultType === ActionResultType.PARTIAL_SUCCESS ? actionResult.submittedRange : undefined
      );
      return Promise.resolve();
    },
    tables: table ? [table] : [],
  });
}

export const createBoxesActionFunction = makeSimpleActionFunction<BoxCreationTableMetadata, {}>({
  label: (params) => getSubmitButtonLabel(params.tableStatus),
  icon: getSubmitActionFunctionIcon(),
  buttonDesign: {
    buttonStyle: (params) => getSubmitButtonStyle(params.tableStatus, ButtonStyle.SECONDARY),
    showTextOnButton: true,
  },
  onInvoke: onInvokeCreateBoxesActionFunction,
  getDisabled: isTableActionButtonDisabled,
  getTooltip: tableActionButtonDisabledTooltip,
  getHidden: (params) => getSubmitButtonHidden(params.tableStatus, params.isCollapsed),
});
